/* =========================================
   20260309sasaki
   inline-product-group.js
   debug rebuild
   - 等高処理は完全撤去
   - 商品描画のみ担当
========================================= */

(function () {
  const CACHE = {};
  const GA_EVENT = "inline_product_click";
  const FEATURE_GA_EVENT = "featured_product_click";

  async function fetchProduct(handle) {
    if (!handle) return null;
    if (CACHE[handle]) return CACHE[handle];

    try {
      const res = await fetch(`/products/${handle}.js`, { credentials: "same-origin" });
      if (!res.ok) return null;

      const data = await res.json();
      CACHE[handle] = data;
      return data;
    } catch (error) {
      return null;
    }
  }

  function formatPrice(price) {
    return new Intl.NumberFormat("ja-JP", {
      style: "currency",
      currency: "JPY"
    }).format((price || 0) / 100);
  }

  function escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function getProductTags(product) {
    if (Array.isArray(product.tags)) return product.tags;
    if (typeof product.tags === "string") {
      return product.tags.split(",").map((tag) => tag.trim()).filter(Boolean);
    }

    return [];
  }

  function getIconLabel(tag) {
    if (!tag || !tag.includes("icon_")) return "";

    return tag.split("_").pop().trim();
  }

  function hasFreeShippingIcon(product) {
    return getProductTags(product).some((tag) => tag.includes("icon_") && tag.includes("\u9001\u6599\u7121\u6599"));
  }

  function hasFreeShippingLabel(product) {
    return (product.price || 0) >= 1000000 || hasFreeShippingIcon(product);
  }

  function isDiscountIcon(tag) {
    return /off/i.test(tag) || tag.includes("\u30af\u30fc\u30dd\u30f3");
  }

  function renderIconTags(product, className = "sp-inline-products__tags") {
    const labels = [];

    if (hasFreeShippingLabel(product)) {
      labels.push({
        label: "\u9001\u6599\u7121\u6599",
        discount: false
      });
    }

    getProductTags(product)
      .map((tag) => ({
        label: getIconLabel(tag),
        discount: isDiscountIcon(tag)
      }))
      .filter((item) => item.label && item.label !== "\u9001\u6599\u7121\u6599")
      .forEach((item) => labels.push(item));

    if (!labels.length) return "";

    return `
      <div class="tagIcon-flex ${className}">
        ${labels.map((item) => `
          <span class="ProductItem__Label tagIcons${item.discount ? " _discount" : ""}">${escapeHtml(item.label)}</span>
        `).join("")}
      </div>
    `;
  }

  function getVendorUrl(vendor) {
    const vendorName = String(vendor || "").trim();
    if (!vendorName) return "";

    const vendorCollections = window.SP_VENDOR_COLLECTIONS || {};
    return vendorCollections[vendorName] || `/collections/vendors?q=${encodeURIComponent(vendorName)}`;
  }

  function renderVendor(product, className = "sp-inline-products__meta vendor") {
    const vendor = String(product.vendor || "").trim();
    if (!vendor) return "";

    return `<a class="${className}" href="${getVendorUrl(vendor)}">${escapeHtml(vendor)}</a>`;
  }

  function buildSchema(products) {
    const items = products.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Product",
        name: p.title,
        image: p.images?.[0] || "",
        offers: {
          "@type": "Offer",
          price: ((p.price || 0) / 100).toString(),
          priceCurrency: "JPY",
          availability: p.available
            ? "https://schema.org/InStock"
            : "https://schema.org/OutOfStock",
          url: `/products/${p.handle}`
        }
      }
    }));

    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: items
    };
  }

  function sendGA(product) {
    if (!window.dataLayer) return;

    window.dataLayer.push({
      event: GA_EVENT,
      item_name: product.title,
      item_id: product.id,
      item_brand: product.vendor
    });
  }

  function sendFeaturedGA(product, label) {
    if (!window.dataLayer) return;

    window.dataLayer.push({
      event: FEATURE_GA_EVENT,
      item_name: product.title,
      item_id: product.id,
      item_brand: product.vendor,
      item_handle: product.handle,
      article_label: label || ""
    });
  }

  function createCard(product) {
    const card = document.createElement("article");
    card.className = "sp-inline-products__item";

    const soldOut = !product.available;
    const productUrl = `/products/${product.handle}`;
    const imageUrl = product.images?.[0] || "";
    const compareAt = product.compare_at_price && product.compare_at_price > product.price
      ? formatPrice(product.compare_at_price)
      : "";
    const vendor = renderVendor(product);
    const iconTags = renderIconTags(product);

    card.innerHTML = `
      <a class="sp-inline-products__media" href="${productUrl}" aria-label="${escapeHtml(product.title)}">
        <div class="sp-inline-products__image-wrap">
          ${imageUrl ? `<img class="sp-inline-products__image" src="${imageUrl}" alt="${escapeHtml(product.title)}" loading="lazy">` : ""}
        </div>
        ${soldOut ? `<span class="sp-inline-products__badge">SOLD OUT</span>` : ``}
      </a>

      <div class="sp-inline-products__body">
        <div class="sp-inline-products__content">
          <div class="sp-inline-products__name">${escapeHtml(product.title)}</div>
          ${iconTags}

          <div class="sp-inline-products__price-row">
            <div class="sp-inline-products__price">${formatPrice(product.price)}</div>
            ${compareAt ? `<div class="sp-inline-products__compare">${compareAt}</div>` : ``}
          </div>

          ${vendor}
        </div>

        ${
          soldOut
            ? `<span class="sp-inline-products__button" aria-disabled="true">商品を見る</span>`
            : `<a class="sp-inline-products__button" href="${productUrl}">商品を見る</a>`
        }
      </div>
    `;

    if (soldOut) {
      card.classList.add("is-soldout");
    } else {
      const button = card.querySelector(".sp-inline-products__button");
      if (button) {
        button.addEventListener("click", function () {
          sendGA(product);
        });
      }
    }

    return card;
  }

  function createLoadingCard() {
    const div = document.createElement("div");
    div.className = "sp-inline-products__loading";
    div.innerHTML = `
      <div class="sp-inline-products__skeleton-thumb"></div>
      <div>
        <div class="sp-inline-products__skeleton-line w-90"></div>
        <div class="sp-inline-products__skeleton-line w-60"></div>
        <div class="sp-inline-products__skeleton-line w-40"></div>
      </div>
    `;
    return div;
  }

  function createFeatureLoadingCard() {
    const div = document.createElement("div");
    div.className = "sp-feature-product__card sp-feature-product__card--loading";
    div.innerHTML = `
      <div class="sp-inline-products__skeleton-thumb"></div>
      <div class="sp-feature-product__loading-lines">
        <div class="sp-inline-products__skeleton-line w-90"></div>
        <div class="sp-inline-products__skeleton-line w-60"></div>
        <div class="sp-inline-products__skeleton-line w-40"></div>
      </div>
    `;
    return div;
  }

  function createFeatureStoryMedia(product) {
    const imageUrl = product.images?.[0] || "";
    if (!imageUrl) return null;

    const media = document.createElement("a");
    media.className = "sp-feature-product__story-media";
    media.href = `/products/${product.handle}`;
    media.setAttribute("aria-label", escapeHtml(product.title));
    media.innerHTML = `
      <span class="sp-feature-product__story-image-wrap">
        <img class="sp-feature-product__story-image" src="${imageUrl}" alt="${escapeHtml(product.title)}" loading="lazy">
      </span>
    `;

    return media;
  }

  function insertFeatureStoryMedia(el, product) {
    const story = el.querySelector(".sp-feature-product__story");
    if (!story || story.querySelector(".sp-feature-product__story-media")) return;

    const media = createFeatureStoryMedia(product);
    if (!media) return;

    const heading = story.querySelector("h3");
    if (heading) {
      const numberLabel = heading.nextElementSibling;
      const insertAfter = numberLabel?.classList?.contains("sp-feature-product__number") ? numberLabel : heading;
      insertAfter.insertAdjacentElement("afterend", media);
    } else {
      story.insertBefore(media, story.firstChild);
    }
  }

  function createFeatureCard(product, label) {
    const card = document.createElement("aside");
    card.className = "sp-feature-product__card";

    const soldOut = !product.available;
    const productUrl = `/products/${product.handle}`;
    const imageUrl = product.images?.[1] || product.images?.[0] || "";
    const compareAt = product.compare_at_price && product.compare_at_price > product.price
      ? formatPrice(product.compare_at_price)
      : "";
    const vendor = renderVendor(product, "sp-feature-product__vendor");
    const iconTags = renderIconTags(product, "sp-feature-product__tags");

    card.innerHTML = `
      <a class="sp-feature-product__media" href="${productUrl}" aria-label="${escapeHtml(product.title)}">
        <div class="sp-feature-product__image-wrap">
          ${imageUrl ? `<img class="sp-feature-product__image" src="${imageUrl}" alt="${escapeHtml(product.title)}" loading="lazy">` : ""}
        </div>
        ${soldOut ? `<span class="sp-feature-product__badge">SOLD OUT</span>` : ``}
      </a>

      <div class="sp-feature-product__info">
        ${vendor}
        <p class="sp-feature-product__name">${escapeHtml(product.title)}</p>
        ${iconTags}

        <div class="sp-feature-product__price-row">
          <span class="sp-feature-product__price">${formatPrice(product.price)}</span>
          ${compareAt ? `<span class="sp-feature-product__compare">${compareAt}</span>` : ``}
        </div>

        ${
          soldOut
            ? `<span class="sp-feature-product__button" aria-disabled="true">商品を見る</span>`
            : `<a class="sp-feature-product__button" href="${productUrl}">商品を見る</a>`
        }
      </div>
    `;

    if (soldOut) {
      card.classList.add("is-soldout");
    } else {
      const button = card.querySelector(".sp-feature-product__button");
      if (button) {
        button.addEventListener("click", function () {
          sendFeaturedGA(product, label);
        });
      }
    }

    return card;
  }

  async function renderBlock(el) {
    const handlesNode = el.querySelector(".sp-inline-products__handles");
    if (!handlesNode) return;

    const handles = handlesNode.textContent
      .split("\n")
      .map((h) => h.trim())
      .filter(Boolean)
      .filter((handle) => /^[a-zA-Z0-9\-_]+$/.test(handle));

    const title = el.dataset.title ? el.dataset.title.trim() : "";

    if (title) {
      const heading = document.createElement("h3");
      heading.className = "sp-inline-products__title";
      heading.innerHTML = title;
      el.appendChild(heading);
    }

    const list = document.createElement("div");
    list.className = "sp-inline-products__list";
    el.appendChild(list);

    const skeletonCount =
      el.dataset.layout === "scroll"
        ? Math.min(handles.length || 3, 4)
        : Math.min(handles.length || 2, 2);

    for (let i = 0; i < skeletonCount; i += 1) {
      list.appendChild(createLoadingCard());
    }

    const products = [];
    const fetched = await Promise.all(handles.map((handle) => fetchProduct(handle)));

    list.innerHTML = "";

    fetched.forEach((product) => {
      if (!product) return;
      products.push(product);
      list.appendChild(createCard(product));
    });

    if (!products.length) {
      const empty = document.createElement("div");
      empty.className = "sp-inline-products__empty";
      empty.textContent = "商品を読み込めませんでした。handle を確認してください。";
      el.appendChild(empty);
      return;
    }

    const schema = buildSchema(products);
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema);
    el.appendChild(script);

    el.classList.add("is-ready");
  }

  async function renderFeatureProduct(el) {
    const handle = String(el.dataset.handle || "").trim();
    if (!/^[a-zA-Z0-9\-_]+$/.test(handle)) return null;

    const loading = createFeatureLoadingCard();
    el.appendChild(loading);

    const product = await fetchProduct(handle);
    loading.remove();

    if (!product) {
      const empty = document.createElement("div");
      empty.className = "sp-feature-product__empty";
      empty.textContent = "商品を読み込めませんでした。handle を確認してください。";
      el.appendChild(empty);
      return null;
    }

    insertFeatureStoryMedia(el, product);
    el.appendChild(createFeatureCard(product, el.dataset.gaLabel || ""));
    el.classList.add("is-ready");
    return product;
  }

  function appendFeatureSchema(products) {
    if (!products.length || document.getElementById("sp-feature-products-schema")) return;

    const script = document.createElement("script");
    script.id = "sp-feature-products-schema";
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(buildSchema(products));
    document.body.appendChild(script);
  }

  function init() {
    const blocks = document.querySelectorAll("[data-sp-inline-products]");
    blocks.forEach((block) => {
      renderBlock(block);
    });

    const featureBlocks = Array.from(document.querySelectorAll("[data-sp-feature-product]"));
    if (featureBlocks.length) {
      Promise.all(featureBlocks.map((block) => renderFeatureProduct(block))).then((products) => {
        appendFeatureSchema(products.filter(Boolean));
      });
    }
  }

  document.addEventListener("DOMContentLoaded", init);
})();
